import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // Nur im Speicher, nur solange der Force-Change-Dialog gebraucht wird.
    // Wird beim Reload absichtlich verloren → dann muss der Nutzer erneut
    // einloggen und der Force-Dialog nutzt den frisch getippten Wert.
    const [pendingLoginPassword, setPendingLoginPassword] = useState("");

    const checkAuth = useCallback(async () => {
        try {
            const r = await api.get("/auth/me");
            setUser(r.data);
        } catch (_) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: Skip auth check if returning from OAuth callback
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const loginWithEmail = async (email, password) => {
        const r = await api.post("/auth/login", { email, password });
        if (r.data.token) setToken(r.data.token);
        setUser(r.data);
        setPendingLoginPassword(password);
        return r.data;
    };

    const registerEmail = async (email, password, name) => {
        const r = await api.post("/auth/register", { email, password, name });
        if (r.data.token) setToken(r.data.token);
        setUser(r.data);
        return r.data;
    };

    const exchangeGoogleSession = async (session_id) => {
        const r = await api.post("/auth/google/session", { session_id });
        if (r.data.token) setToken(r.data.token);
        setUser(r.data);
        return r.data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_) {}
        setToken(null);
        setUser(null);
        setPendingLoginPassword("");
        localStorage.removeItem("inventur_tenant");
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                loginWithEmail,
                registerEmail,
                exchangeGoogleSession,
                logout,
                refresh: checkAuth,
                pendingLoginPassword,
                clearPendingLoginPassword: () => setPendingLoginPassword(""),
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
